import type { IUserService, TermsSurface } from "@/services/interfaces/IUserService";
import type {
  UserProfile,
  UserRole,
  PublicProfile,
  ReputationDetail,
  LeaderboardEntry,
  PlatformStats,
  ActivityLevel,
  TrackRecord,
  QuestInterest,
  QuestInterestKind,
  SetQuestInterestInput,
  QuestAffinity,
  QuestAffinityAnswer,
} from "@/lib/types";
import { toSupportedLanguage } from "@/lib/types";
import { supabase } from "@/lib/supabase";
import { MAX_IMAGE_BYTES, validateImageFile } from "@/lib/questImages";
import {
  PROFILE_IMAGES_BUCKET,
  isOwnPath,
  ownAvatarPath,
  ProfileImageError,
  profileImagePath,
  type ProfileImageKind,
} from "@/lib/profileImages";
import type { PolygonClient } from "@/lib/polygon";
import { toProfileUpdateRow } from "@/lib/profileUpdateColumns";

// ─── Track record mapper ─────────────────────────────────

interface TrackRecordRow {
  worker: Record<string, number | string>;
  creator: Record<string, number | string>;
  task_skills: string[][];
  activity: { day: string; worked: number; paid?: number; volunteer?: number; launched: number }[];
}

/** jsonb numerics arrive as numbers, NUMERIC sums can arrive as strings. */
function num(v: number | string | undefined): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

export function toTrackRecord(row: TrackRecordRow): TrackRecord {
  const w = row.worker ?? {};
  const c = row.creator ?? {};
  return {
    worker: {
      paidTasks: num(w.paid_tasks),
      volunteerTasks: num(w.volunteer_tasks),
      paidHours: num(w.paid_hours),
      volunteerHours: num(w.volunteer_hours),
      questsJoined: num(w.quests_joined),
      creatorsServed: num(w.creators_served),
    },
    creator: {
      questsLaunched: num(c.quests_launched),
      questsDelivered: num(c.quests_delivered),
      questsCompleted: num(c.quests_completed),
      paidQuests: num(c.paid_quests),
      volunteerQuests: num(c.volunteer_quests),
      tasksApproved: num(c.tasks_approved),
      workersHired: num(c.workers_hired),
    },
    taskSkills: (row.task_skills ?? []).map((skills) => [...skills]),
    activity: (row.activity ?? []).map((a) => ({
      day: a.day,
      worked: num(a.worked),
      paid: num(a.paid),
      volunteer: num(a.volunteer),
      launched: num(a.launched),
    })),
  };
}

// ─── Row mapper ──────────────────────────────────────────

interface ProfileRow {
  id: string;
  /** public_profiles only (20261011110000); absent before the migration. */
  account_type?: string | null;
  agent_owner_id?: string | null;
  handle: string | null;
  display_name: string;
  email: string;
  avatar_url: string | null;
  banner_path?: string | null;
  bio: string;
  skills: string[];
  needs: string[];
  wallet_address: string | null;
  role_preference: string;
  recurring_match: boolean | null;
  activity_level: string;
  role: string;
  reputation_overall: number;
  reputation_completion: number;
  reputation_quality: number;
  reputation_speed: number;
  reputation_reliability: number;
  preferred_currency: string;
  preferred_language: string | null;
  is_onboarded: boolean;
  created_at: string;
  updated_at: string;
  location_city: string | null;
  location_country: string | null;
}

/** Public URL of an object in profile-images. The bucket is public by design (20261001100000). */
function profileImageUrl(path: string): string {
  return supabase.storage.from(PROFILE_IMAGES_BUCKET).getPublicUrl(path).data.publicUrl;
}

/** The URL prefix every object in profile-images shares, for recognising our own avatar URLs. */
function profileImagesPublicBase(): string {
  return profileImageUrl("");
}

function mapProfileRow(row: ProfileRow): UserProfile {
  return {
    id: row.id,
    handle: row.handle ?? "",
    displayName: row.display_name,
    email: row.email,
    avatarUrl: row.avatar_url,
    bannerPath: row.banner_path ?? null,
    bannerUrl: row.banner_path ? profileImageUrl(row.banner_path) : null,
    bio: row.bio,
    skills: row.skills ?? [],
    needs: row.needs ?? [],
    walletAddress: row.wallet_address,
    rolePreference: row.role_preference as UserProfile["rolePreference"],
    // DEV-783 step 9. Defaults TRUE at the column level; the ?? mirrors it for
    // a row read before the migration landed rather than flipping an absent
    // value to "no, do not match me", which is not what NULL meant.
    recurringMatch: row.recurring_match ?? true,
    activityLevel: (row.activity_level as ActivityLevel) ?? "casual",
    preferredCurrency: (row.preferred_currency as UserProfile["preferredCurrency"]) ?? "USDT",
    preferredLanguage: toSupportedLanguage(row.preferred_language),
    role: (row.role ?? "user") as UserRole,
    reputation: {
      overall: Number(row.reputation_overall),
      completionRate: Number(row.reputation_completion),
      qualityScore: Number(row.reputation_quality),
      speedScore: Number(row.reputation_speed),
      reliability: Number(row.reputation_reliability),
    },
    locationCity: row.location_city ?? null,
    locationCountry: row.location_country ?? null,
    createdAt: row.created_at,
  };
}

// ─── Quest interest mapper ───────────────────────────────

interface QuestInterestRow {
  kind: string;
  wants_alerts: boolean;
  wants_matching: boolean;
  source_quest_id: string | null;
  answered_at: string;
  updated_at: string;
}

function mapQuestInterest(r: QuestInterestRow): QuestInterest {
  return {
    kind: r.kind as QuestInterestKind,
    wantsAlerts: r.wants_alerts,
    wantsMatching: r.wants_matching,
    sourceQuestId: r.source_quest_id,
    answeredAt: r.answered_at,
    updatedAt: r.updated_at,
  };
}

// ─── Quest affinity mapper ───────────────────────────────

interface QuestAffinityRow {
  quest_id: string | null;
  answer: string;
  category: string;
  skills: string[] | null;
  is_volunteer: boolean;
  updated_at: string;
}

const QUEST_AFFINITY_COLUMNS = "quest_id, answer, category, skills, is_volunteer, updated_at";

function mapQuestAffinity(r: QuestAffinityRow): QuestAffinity {
  return {
    questId: r.quest_id!,
    answer: r.answer as QuestAffinityAnswer,
    category: r.category,
    skills: r.skills ?? [],
    isVolunteer: r.is_volunteer,
    updatedAt: r.updated_at,
  };
}

// ─── Service ─────────────────────────────────────────────

export class SupabaseUserService implements IUserService {
  constructor(private polygonClient: PolygonClient | null = null) {}

  async getProfile(userId: string): Promise<UserProfile> {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (error) throw new Error(`Failed to fetch profile: ${error.message}`);
    return mapProfileRow(data as ProfileRow);
  }

  async updateProfile(
    userId: string,
    data: Partial<UserProfile>,
  ): Promise<UserProfile> {
    // Whitelisted columns only; email is deliberately absent (DEV-803).
    const updates = toProfileUpdateRow(data);

    const { data: row, error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", userId)
      .select()
      .single();

    if (error) throw new Error(`Failed to update profile: ${error.message}`);
    const profile = mapProfileRow(row as ProfileRow);

    // Fire-and-forget: update profile hash on-chain
    // Only the three fields the on-chain profile hash actually covers. Without
    // this gate every preference write -- including usePreferredLanguageSync's
    // language save and its retries -- paid for a chain transaction that
    // rewrote the identical handle/name/bio.
    const changesOnChainProfile =
      data.handle !== undefined || data.displayName !== undefined || data.bio !== undefined;
    if (this.polygonClient && changesOnChainProfile) {
      this.polygonClient
        .updateProfile({
          handle: profile.handle,
          displayName: profile.displayName,
          bio: profile.bio,
        })
        .catch((err) =>
          console.error("[UserService] On-chain profile update failed:", err),
        );
    }

    return profile;
  }

  /**
   * /u/:handle. Through get_public_profile_by_handle (20261016120000), not the
   * view: the page is open to logged-out visitors, and anon may not list
   * public_profiles' handle/skills columns. Same row shape as the view.
   */
  async getPublicProfile(handle: string): Promise<PublicProfile | null> {
    // The RPC returns SETOF — avoid .maybeSingle() which adds the
    // Accept: application/vnd.pgrst.object+json header and triggers
    // "Cannot coerce the result to a single JSON object" on SETOF returns.
    const { data, error } = await supabase
      .rpc("get_public_profile_by_handle", { p_handle: handle });
    if (error)
      throw new Error(`Failed to fetch public profile: ${error.message}`);
    const row = (data as ProfileRow[] | null)?.[0] ?? null;
    return row ? this.toPublicProfile(row) : null;
  }

  async getPublicProfileById(profileId: string): Promise<PublicProfile | null> {
    const { data, error } = await supabase
      .from("public_profiles")
      .select("*")
      .eq("id", profileId)
      .maybeSingle();

    if (error)
      throw new Error(`Failed to fetch public profile: ${error.message}`);
    return data ? this.toPublicProfile(data as ProfileRow) : null;
  }

  private toPublicProfile(row: ProfileRow): PublicProfile {
    return {
      id: row.id,
      handle: row.handle ?? "",
      displayName: row.display_name,
      avatarUrl: row.avatar_url,
      bannerUrl: row.banner_path ? profileImageUrl(row.banner_path) : null,
      bio: row.bio,
      skills: row.skills ?? [],
      rolePreference: (row.role_preference ?? "both") as PublicProfile["rolePreference"],
      reputation: {
        overall: Number(row.reputation_overall),
        completionRate: Number(row.reputation_completion),
        qualityScore: Number(row.reputation_quality),
        speedScore: Number(row.reputation_speed),
        reliability: Number(row.reputation_reliability),
      },
      completedBounties: 0,
      questsCreated: 0,
      memberSince: row.created_at,
      accountType: row.account_type === "agent" ? "agent" : "human",
      agentOwnerId: row.agent_owner_id ?? null,
    };
  }

  async getAgentsOwnedBy(
    ownerId: string,
  ): Promise<{ agentDid: string; name: string; status: "active" | "paused" }[]> {
    const { data, error } = await supabase.rpc("agents_owned_by", {
      p_owner_id: ownerId,
    });
    if (error) throw new Error(`Failed to fetch owned agents: ${error.message}`);
    return ((data ?? []) as { agent_did: string; name: string; status: string }[]).map((r) => ({
      agentDid: r.agent_did,
      name: r.name,
      status: r.status === "paused" ? "paused" : "active",
    }));
  }

  async getTrackRecord(profileId: string): Promise<TrackRecord | null> {
    const { data, error } = await supabase.rpc("get_profile_track_record", {
      p_profile_id: profileId,
    });
    if (error)
      throw new Error(`Failed to fetch track record: ${error.message}`);
    return data ? toTrackRecord(data as TrackRecordRow) : null;
  }

  /** The /u/:handle read, open to anon (20261016120000). The id-keyed RPC
   *  stays members-only because anon can list profile ids. */
  async getTrackRecordForHandle(handle: string, profileId: string): Promise<TrackRecord | null> {
    const { data, error } = await supabase.rpc("get_profile_track_record_for_handle", {
      p_handle: handle,
      p_profile_id: profileId,
    });
    if (error)
      throw new Error(`Failed to fetch track record: ${error.message}`);
    return data ? toTrackRecord(data as TrackRecordRow) : null;
  }

  async updateSkills(userId: string, skills: string[]): Promise<void> {
    const { error } = await supabase
      .from("profiles")
      .update({ skills })
      .eq("id", userId);

    if (error) throw new Error(`Failed to update skills: ${error.message}`);

    // Fire-and-forget: update skills hash on-chain
    if (this.polygonClient) {
      this.polygonClient.updateSkills(skills).catch((err) =>
        console.error("[UserService] On-chain skills update failed:", err),
      );
    }
  }

  /**
   * DEV-829: additive counterpart to updateSkills, for callers that only know a
   * subset of what the user can do.
   *
   * The frictionless quest CTA (useFrictionlessOnboarding) used updateSkills,
   * which assigns the whole column. That is correct for profile editing and
   * onboarding — the user saw the full list and chose it — but the CTA renders
   * for ANY logged-out visitor, including an established worker whose session
   * expired. For them the quest's two skills replaced a career's worth, with no
   * confirmation and no undo, and skills are what the headhunter funnel matches
   * on.
   *
   * Read-modify-write rather than an atomic SQL union: PostgREST cannot express
   * `skills = array(select distinct unnest(skills || $1))` through .update(),
   * so the alternative is an RPC and a migration. The lost-update window is one
   * user's own profile between two of their own writes, and the cost of losing
   * that race is a missing skill, not the wiped list this replaces. If a second
   * writer ever appears, move this into a SECURITY DEFINER function.
   */
  async addSkills(userId: string, skills: string[]): Promise<void> {
    if (skills.length === 0) return;

    const { data, error: readError } = await supabase
      .from("profiles")
      .select("skills")
      .eq("id", userId)
      .single();

    if (readError) throw new Error(`Failed to read skills: ${readError.message}`);

    const existing: string[] = data?.skills ?? [];
    const merged = [...new Set([...existing, ...skills])];

    // Nothing new to say. Skipping the write also skips the on-chain hash
    // update below, which costs gas for a no-op.
    if (merged.length === existing.length) return;

    const { error } = await supabase
      .from("profiles")
      .update({ skills: merged })
      .eq("id", userId);

    if (error) throw new Error(`Failed to add skills: ${error.message}`);

    // Fire-and-forget: update skills hash on-chain, with the merged list — the
    // hash must describe the profile, not the increment.
    if (this.polygonClient) {
      this.polygonClient.updateSkills(merged).catch((err) =>
        console.error("[UserService] On-chain skills update failed:", err),
      );
    }
  }

  /**
   * The user's on-chain reputation, or `null` when they have no on-chain
   * identity to read (no wallet, or never registered on the active chain).
   *
   * Deliberately throws on an RPC failure rather than returning zeros.
   *
   * This method used to swallow every chain error and fall through to
   * `public_profiles.reputation_*`. Those five columns are NOT NULL DEFAULT 0
   * and nothing in the repo has ever written them (a migration actively refuses
   * a self-write), so the fallback could only ever return a perfect zero on
   * every axis. That is indistinguishable from a real failure — and worse, it
   * renders as a confident "0/100" verdict on a worker who has done nothing
   * wrong. An empty card and an error card are both honest; a fabricated zero
   * is not. The fallback is gone; callers get a thrown error to surface.
   */
  async getReputation(userId: string): Promise<ReputationDetail | null> {
    if (!this.polygonClient) return null;

    // `get_user_wallet` is a SECURITY DEFINER RPC precisely so this works for
    // another user's profile — `profiles` RLS is owner-or-admin, and a denied
    // embed returns 200 + null rather than an error.
    const { data: wallet, error } = await supabase.rpc("get_user_wallet", {
      p_user_id: userId,
    });

    if (error) {
      throw new Error(`Failed to resolve wallet: ${error.message}`);
    }
    if (!wallet) return null;

    const detail = await this.polygonClient.getReputation(wallet);

    // lastUpdated is only ever written by the ledger itself; zero means this
    // address was never passed to initializeReputation on THIS chain. Reading
    // an unregistered address returns a zero-filled struct, which would
    // otherwise render as a genuine 0/100.
    if (detail.lastUpdated === 0) return null;

    return detail;
  }

  async getLeaderboard(_category?: string): Promise<LeaderboardEntry[]> {
    const { data, error } = await supabase
      .from("public_profiles")
      .select("id, display_name, avatar_url, reputation_overall")
      .order("reputation_overall", { ascending: false })
      .limit(50);

    if (error)
      throw new Error(`Failed to fetch leaderboard: ${error.message}`);

    return (data ?? []).map((row) => ({
      userId: row.id,
      displayName: row.display_name,
      avatarUrl: row.avatar_url,
      reputation: Number(row.reputation_overall),
      completedBounties: 0,
    }));
  }

  async setProfileImage(userId: string, kind: ProfileImageKind, file: File): Promise<UserProfile> {
    // Before any network call. The bucket enforces the same limits; this is
    // about failing in the picker, not after 5 MB crossed the wire.
    const check = validateImageFile(file);
    if (!check.ok) {
      throw new ProfileImageError(check.reason === "too_large" ? "too_large" : "bad_type",
        check.reason === "too_large"
          ? `Image must be ${Math.floor(MAX_IMAGE_BYTES / 1024 / 1024)} MB or smaller`
          : "Images must be WebP, PNG or JPEG");
    }

    const before = await this.getProfile(userId);
    const path = profileImagePath(userId, kind, file.type, crypto.randomUUID());
    const { error: uploadError } = await supabase.storage
      .from(PROFILE_IMAGES_BUCKET)
      .upload(path, file, { contentType: file.type });
    if (uploadError) throw new ProfileImageError("upload_failed", `Failed to upload image: ${uploadError.message}`);

    const updates = kind === "avatar" ? { avatar_url: profileImageUrl(path) } : { banner_path: path };
    const { data: row, error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", userId)
      .select()
      .single();
    if (error) {
      // The row still points at the old picture; drop the orphan we just made.
      await supabase.storage.from(PROFILE_IMAGES_BUCKET).remove([path]);
      throw new ProfileImageError("upload_failed", `Failed to update profile: ${error.message}`);
    }

    await this.removeReplaced(userId, kind, before);
    return mapProfileRow(row as ProfileRow);
  }

  async clearProfileImage(userId: string, kind: ProfileImageKind): Promise<UserProfile> {
    const before = await this.getProfile(userId);
    const updates = kind === "avatar" ? { avatar_url: null } : { banner_path: null };
    const { data: row, error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", userId)
      .select()
      .single();
    if (error) throw new Error(`Failed to update profile: ${error.message}`);
    await this.removeReplaced(userId, kind, before);
    return mapProfileRow(row as ProfileRow);
  }

  /**
   * Best effort, after the row has moved on: a failure leaves an unlinked
   * public object behind (see the migration's NOT COVERED), never a profile
   * pointing at nothing. Only ever our own objects: a provider avatar URL is
   * not ours to delete.
   */
  private async removeReplaced(userId: string, kind: ProfileImageKind, before: UserProfile): Promise<void> {
    const old =
      kind === "avatar"
        ? ownAvatarPath(before.avatarUrl, userId, profileImagesPublicBase())
        : isOwnPath(before.bannerPath, userId) ? before.bannerPath : null;
    if (!old) return;
    const { error } = await supabase.storage.from(PROFILE_IMAGES_BUCKET).remove([old]);
    if (error) console.warn("[profile-images] replaced picture not removed", old, error);
  }

  async markOnboarded(userId: string): Promise<void> {
    const { error } = await supabase
      .from("profiles")
      .update({ is_onboarded: true })
      .eq("id", userId);

    if (error) throw new Error(`Failed to mark onboarded: ${error.message}`);
  }

  async recordTermsAcceptance(userId: string, version: string, surface: TermsSurface): Promise<void> {
    // ignoreDuplicates -> ON CONFLICT DO NOTHING: the first acceptance of a
    // version is the record; a later login must not overwrite its surface.
    // accepted_at is omitted on purpose -- the column is server-time only.
    const { error } = await supabase
      .from("terms_acceptances")
      .upsert(
        { profile_id: userId, terms_version: version, surface },
        { onConflict: "profile_id,terms_version", ignoreDuplicates: true },
      );

    if (error) throw new Error(`Failed to record terms acceptance: ${error.message}`);
  }

  async getPlatformStats(): Promise<PlatformStats> {
    // TODO: Replace with real aggregate queries when tables exist
    return {
      activeContributors: 127,
      tasksCompletedThisWeek: 342,
      totalPaymentsReleased: 48500,
    };
  }

  async getCollaboratorCount(skills: string[], needs: string[]): Promise<number> {
    // TODO: Replace with real query matching skills/needs across users
    return Math.max(3, Math.floor(skills.length * 7 + needs.length * 5));
  }

  async getQuestInterest(userId: string, kind: QuestInterestKind): Promise<QuestInterest | null> {
    // RLS returns the caller's own row, or EVERY row for an admin, so the
    // profile is pinned explicitly: an admin must never read someone else's
    // answer as their own.
    const { data, error } = await supabase
      .from("quest_interest")
      .select("kind, wants_alerts, wants_matching, source_quest_id, answered_at, updated_at")
      .eq("profile_id", userId)
      .eq("kind", kind)
      .maybeSingle();
    if (error) throw new Error(`Failed to fetch quest interest: ${error.message}`);
    return data ? mapQuestInterest(data as QuestInterestRow) : null;
  }

  async setQuestInterest(input: SetQuestInterestInput): Promise<QuestInterest> {
    // SECURITY DEFINER (20260923120000): writes the caller's own row only and
    // refuses a source quest they did not complete. It never touches
    // recurring_match.
    const { data, error } = await supabase
      .rpc("set_quest_interest", {
        p_kind: input.kind,
        p_wants_alerts: input.wantsAlerts,
        p_wants_matching: input.wantsMatching,
        p_source_quest_id: input.sourceQuestId ?? null,
      })
      .single();
    if (error) throw new Error(error.message);
    return mapQuestInterest(data as QuestInterestRow);
  }

  async getQuestAffinities(userId: string): Promise<QuestAffinity[]> {
    // Pinned to the caller: RLS hands an admin every worker's answers.
    const { data, error } = await supabase
      .from("quest_affinity")
      .select(QUEST_AFFINITY_COLUMNS)
      .eq("profile_id", userId)
      .order("updated_at", { ascending: false });
    if (error) throw new Error(`Failed to fetch quest affinity: ${error.message}`);
    return ((data ?? []) as QuestAffinityRow[]).map(mapQuestAffinity);
  }

  async setQuestAffinity(
    questId: string,
    answer: QuestAffinityAnswer | null,
  ): Promise<QuestAffinity | null> {
    // SECURITY DEFINER (20261020100000): writes the caller's own row and
    // snapshots the quest's features server-side; NULL deletes it.
    const { data, error } = await supabase
      .rpc("set_quest_affinity", { p_quest_id: questId, p_answer: answer })
      .maybeSingle();
    if (error) throw new Error(error.message);
    // A NULL composite comes back as a row of nulls, not as null.
    const row = data as QuestAffinityRow | null;
    return row?.quest_id ? mapQuestAffinity(row) : null;
  }

  async getAttentionDigestEnabled(userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from("profiles")
      .select("attention_digest_enabled")
      .eq("id", userId)
      .single();
    if (error) throw new Error(error.message);
    return (data as { attention_digest_enabled: boolean }).attention_digest_enabled;
  }

  async setAttentionDigestEnabled(userId: string, enabled: boolean): Promise<void> {
    // Column-level UPDATE grant (20261007130000) plus the owner RLS policy:
    // a write to anyone else's row matches zero rows.
    const { data, error } = await supabase
      .from("profiles")
      .update({ attention_digest_enabled: enabled })
      .eq("id", userId)
      .select("id");
    if (error) throw new Error(error.message);
    // RLS turns a write it refuses (or a missing row) into zero rows, not an
    // error. Count them so the switch snaps back with a message instead of
    // showing a state the server never stored.
    if (!data || data.length === 0) {
      throw new Error("Daily summary setting was not saved");
    }
  }
}
